import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bot, CheckCircle2, KeyRound, Pencil, Plug, Plus, Star, Trash2, XCircle, Zap } from '@/components/icons';
import {
  LLM_PROVIDERS,
  LLM_PROVIDER_SPECS,
  LLM_SECRET_FIELDS,
  missingLlmFields,
  type LlmConfigCreate,
  type LlmConfigUpdate,
  type LlmFieldSpec,
  type LlmProvider,
  type LlmSecretField,
} from '@1qa/shared';
import { ApiError, api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import type { LlmConfigView, LlmTestResult } from '@/lib/types';
import { ScopePicker } from '@/components/ScopePicker';
import bedrockLogo from '@/assets/brands/amazon-bedrock.svg';
import anthropicLogo from '@/assets/brands/anthropic.svg';
import geminiLogo from '@/assets/brands/google-gemini.svg';
import litellmDarkLogo from '@/assets/brands/litellm-dark.svg';
import litellmLogo from '@/assets/brands/litellm.svg';
import openaiLogo from '@/assets/brands/openai.svg';
import { useScope } from '@/lib/scope';
import { Badge, Button, Card, Dialog, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, Toggle, useToast } from '@/components/ui';

/**
 * Official mark per provider (src/assets/brands, source noted in each file). One-colour marks (OpenAI,
 * Anthropic) are drawn as a mask in the text colour so they read in both themes; LiteLLM ships its own
 * dark-background version.
 */
const PROVIDER_LOGO: Record<LlmProvider, { src: string; mono?: boolean; darkSrc?: string }> = {
  OPENAI: { src: openaiLogo, mono: true },
  ANTHROPIC: { src: anthropicLogo, mono: true },
  BEDROCK: { src: bedrockLogo },
  LITELLM: { src: litellmLogo, darkSrc: litellmDarkLogo },
  GEMINI: { src: geminiLogo },
};

function ProviderTile({ provider, size = 'md' }: { provider: LlmProvider; size?: 'md' | 'lg' }) {
  const logo = PROVIDER_LOGO[provider];
  const mark = size === 'lg' ? 'size-7' : 'size-6';
  return (
    <span
      aria-hidden
      className={clsx(
        'grid shrink-0 place-items-center rounded-card bg-surface-sunken ring-1 ring-border ring-inset',
        size === 'lg' ? 'size-12' : 'size-11',
      )}
    >
      {logo.mono ? (
        <span
          className={clsx(mark, 'bg-fg')}
          style={{ maskImage: `url("${logo.src}")`, WebkitMaskImage: `url("${logo.src}")`, maskSize: 'contain', WebkitMaskSize: 'contain', maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center' }}
        />
      ) : logo.darkSrc ? (
        <>
          <img src={logo.src} alt="" className={clsx(mark, 'object-contain dark:hidden')} />
          <img src={logo.darkSrc} alt="" className={clsx(mark, 'hidden object-contain dark:block')} />
        </>
      ) : (
        <img src={logo.src} alt="" className={clsx(mark, 'object-contain')} />
      )}
    </span>
  );
}

type PlainKey = 'model' | 'baseUrl' | 'region';
type SecretState = Record<LlmSecretField, { value: string; remove: boolean }>;

const emptySecrets = (): SecretState =>
  Object.fromEntries(LLM_SECRET_FIELDS.map((f) => [f, { value: '', remove: false }])) as SecretState;

const isSecretKey = (k: LlmFieldSpec['key']): k is LlmSecretField => (LLM_SECRET_FIELDS as readonly string[]).includes(k);

/** Reads a list of missing field labels from a 400 MISSING_FIELDS error, whatever shape the API used. */
function missingFrom(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.code !== 'MISSING_FIELDS') return [];
  const d = err.details as unknown;
  const arr = Array.isArray(d) ? d : Array.isArray((d as { fields?: unknown })?.fields) ? (d as { fields: unknown[] }).fields : [];
  return arr.map(String);
}

function ConfigDialog({
  open,
  onClose,
  existing,
  orgId,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  existing: LlmConfigView | null;
  orgId: string;
  /** Scope for new models: null = whole organization. */
  projectId: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [provider, setProvider] = useState<LlmProvider | null>(existing?.provider ?? null);
  const [name, setName] = useState('');
  const [plain, setPlain] = useState<Record<PlainKey, string>>({ model: '', baseUrl: '', region: '' });
  const [secrets, setSecrets] = useState<SecretState>(emptySecrets);
  const [makeDefault, setMakeDefault] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProvider(existing?.provider ?? null);
    setName(existing?.name ?? '');
    setPlain({ model: existing?.model ?? '', baseUrl: existing?.baseUrl ?? '', region: existing?.region ?? '' });
    setSecrets(emptySecrets());
    setMakeDefault(false);
    setShowErrors(false);
  }, [open, existing]);

  const choose = (p: LlmProvider) => {
    setProvider(p);
    const spec = LLM_PROVIDER_SPECS[p];
    setName((n) => n || spec.label);
    setPlain((v) => ({ ...v, model: v.model || spec.defaultModel || '' }));
  };

  const spec = provider ? LLM_PROVIDER_SPECS[provider] : null;

  // Which secrets will be set after saving (existing ones count unless removed).
  const secretsSet = useMemo(() => {
    const out: Partial<Record<LlmSecretField, boolean>> = {};
    for (const f of LLM_SECRET_FIELDS) {
      const s = secrets[f];
      out[f] = s.value.trim() ? true : s.remove ? false : !!existing?.secretsSet?.[f];
    }
    return out;
  }, [secrets, existing]);

  const missing = provider
    ? missingLlmFields(provider, { model: plain.model.trim(), baseUrl: plain.baseUrl.trim(), region: plain.region.trim() }, secretsSet)
    : [];

  const save = useMutation({
    mutationFn: async () => {
      if (!provider || !spec) throw new Error('Choose a provider first.');
      const fieldKeys = new Set(spec.fields.map((f) => f.key));
      const optionalPlain = (k: 'baseUrl' | 'region') => (fieldKeys.has(k) ? plain[k].trim() || null : undefined);
      if (!existing) {
        const body: LlmConfigCreate = {
          name: name.trim(),
          provider,
          model: plain.model.trim(),
          baseUrl: optionalPlain('baseUrl'),
          region: optionalPlain('region'),
          secrets: Object.fromEntries(
            LLM_SECRET_FIELDS.filter((f) => fieldKeys.has(f) && secrets[f].value.trim()).map((f) => [f, secrets[f].value.trim()]),
          ),
          enabled: true,
          isDefault: makeDefault,
          projectId,
        };
        return api.createLlmConfig(orgId, body);
      }
      const secretPatch: NonNullable<LlmConfigUpdate['secrets']> = {};
      for (const f of LLM_SECRET_FIELDS) {
        if (!fieldKeys.has(f)) continue;
        if (secrets[f].value.trim()) secretPatch[f] = secrets[f].value.trim();
        else if (secrets[f].remove) secretPatch[f] = null;
      }
      const body: LlmConfigUpdate = {
        name: name.trim(),
        model: plain.model.trim(),
        baseUrl: optionalPlain('baseUrl'),
        region: optionalPlain('region'),
        ...(Object.keys(secretPatch).length ? { secrets: secretPatch } : {}),
      };
      return api.updateLlmConfig(existing.id, body);
    },
    onSuccess: (saved) => {
      toast.success(existing ? `${saved.name} updated` : `${saved.name} added`);
      qc.invalidateQueries({ queryKey: ['llm-configs'] });
      qc.invalidateQueries({ queryKey: ['llm-status'] });
      onClose();
    },
  });

  const serverMissing = missingFrom(save.error);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (!name.trim() || missing.length) return;
    save.mutate();
  };

  const fieldError = (f: LlmFieldSpec): string | undefined => {
    if (!showErrors) return undefined;
    if (missing.includes(f.label) || serverMissing.includes(f.label) || serverMissing.includes(f.key)) return `${f.label.replace(/ \(optional\)$/, '')} is required`;
    return undefined;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={existing ? `Edit ${existing.name}` : spec ? `Add ${spec.label} model` : 'Add an AI model'}
      description={
        spec ? spec.description : 'Choose where 1QA should send plain-English steps. You can add several and pick one as the default.'
      }
      footer={
        spec && (
          <>
            {!existing && (
              <Button variant="ghost" className="mr-auto" onClick={() => setProvider(null)}>
                Change provider
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="llm-config-form" loading={save.isPending}>
              {existing ? 'Save changes' : 'Add model'}
            </Button>
          </>
        )
      }
    >
      {!spec ? (
        <div className="grid gap-3 sm:grid-cols-2" role="list" aria-label="AI providers">
          {LLM_PROVIDERS.map((p) => {
            const s = LLM_PROVIDER_SPECS[p];
            return (
              <button
                key={p}
                type="button"
                role="listitem"
                onClick={() => choose(p)}
                className="flex items-start gap-3 rounded-card bg-surface p-4 text-left ring-1 ring-border ring-inset transition hover:ring-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              >
                <ProviderTile provider={p} />
                <span className="min-w-0">
                  <span className="block font-semibold text-fg">{s.label}</span>
                  <span className="mt-0.5 block text-xs text-fg-secondary">{s.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <form id="llm-config-form" onSubmit={submit} className="space-y-5" noValidate>
          <Field label="Display name" hint="Shown to Admins, e.g. “Claude for staging”." error={showErrors && !name.trim() ? 'Give this model a name' : undefined}>
            {(id) => <Input id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} invalid={showErrors && !name.trim()} />}
          </Field>

          {spec.fields.map((f) => {
            if (isSecretKey(f.key)) {
              const key = f.key;
              const saved = !!existing?.secretsSet?.[key];
              const s = secrets[key];
              return (
                <Field
                  key={key}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <KeyRound className="size-3 text-fg-muted" aria-hidden /> {f.label}
                    </span>
                  }
                  error={fieldError(f)}
                  hint={
                    saved && !s.remove
                      ? '•••• saved — leave empty to keep the current value.'
                      : (f.help ?? 'Stored encrypted. It is never shown again after saving.')
                  }
                >
                  {(id) => (
                    <div className="flex gap-2">
                      <Input
                        id={id}
                        type="password"
                        autoComplete="off"
                        spellCheck={false}
                        className="font-mono"
                        placeholder={saved && !s.remove ? '••••••••••••' : f.placeholder}
                        value={s.value}
                        invalid={!!fieldError(f)}
                        onChange={(e) => setSecrets((all) => ({ ...all, [key]: { value: e.target.value, remove: false } }))}
                      />
                      {saved && (
                        <Button
                          variant={s.remove ? 'secondary' : 'ghost'}
                          onClick={() => setSecrets((all) => ({ ...all, [key]: { value: '', remove: !all[key].remove } }))}
                          aria-pressed={s.remove}
                        >
                          {s.remove ? 'Undo remove' : 'Remove'}
                        </Button>
                      )}
                    </div>
                  )}
                </Field>
              );
            }
            const key = f.key as PlainKey;
            return (
              <Field key={key} label={f.label} hint={f.help} error={fieldError(f)}>
                {(id) => (
                  <Input
                    id={id}
                    value={plain[key]}
                    placeholder={f.placeholder}
                    spellCheck={false}
                    className={key !== 'region' ? 'font-mono' : undefined}
                    invalid={!!fieldError(f)}
                    onChange={(e) => setPlain((v) => ({ ...v, [key]: e.target.value }))}
                  />
                )}
              </Field>
            );
          })}

          {!existing && (
            <label className="flex items-center gap-3 text-sm text-fg">
              <Toggle checked={makeDefault} onChange={setMakeDefault} label="Use as the default model" />
              Use as the default model for all test runs
            </label>
          )}

          {save.isError && (
            <div role="alert" className="rounded-card bg-danger-subtle p-3.5 text-sm text-danger-text ring-1 ring-danger-line ring-inset">
              {errorMessage(save.error)}
              {serverMissing.length > 0 && <span className="block pt-1">Missing: {serverMissing.join(', ')}</span>}
            </div>
          )}
        </form>
      )}
    </Dialog>
  );
}

function TestOutcome({ result, config }: { result?: LlmTestResult; config: LlmConfigView }) {
  const ok = result ? result.ok : config.lastTestOk;
  const message = result ? result.message : config.lastTestMessage;
  if (ok === null || ok === undefined) {
    return <p className="text-xs text-fg-muted">Not tested yet - use “Test connection”.</p>;
  }
  return (
    <p className={clsx('flex items-start gap-1.5 text-xs', ok ? 'text-success' : 'text-danger-text')}>
      {ok ? <CheckCircle2 className="mt-px size-3.5 shrink-0" /> : <XCircle className="mt-px size-3.5 shrink-0" />}
      <span className="min-w-0 break-words">
        {ok ? 'Connected' : 'Failed'}
        {message ? ` - ${message}` : ''}
        {result?.latencyMs !== undefined && ` (${result.latencyMs} ms)`}
        {!result && config.lastTestAt && <span className="text-fg-muted"> · {timeAgo(config.lastTestAt)}</span>}
      </span>
    </p>
  );
}

function ConfigCard({ config, onEdit, onDelete }: { config: LlmConfigView; onEdit: () => void; onDelete: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [result, setResult] = useState<LlmTestResult | undefined>();
  const spec = LLM_PROVIDER_SPECS[config.provider];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['llm-configs'] });
    qc.invalidateQueries({ queryKey: ['llm-status'] });
  };

  const test = useMutation({
    mutationFn: () => api.testLlmConfig(config.id),
    onMutate: () => setResult(undefined),
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
    onError: (e) => setResult({ ok: false, message: errorMessage(e) }),
  });

  const makeDefault = useMutation({
    mutationFn: () => api.makeDefaultLlmConfig(config.id),
    onSuccess: () => {
      toast.success(`${config.name} is now the default model`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.updateLlmConfig(config.id, { enabled }),
    onSuccess: (c) => {
      toast.success(`${c.name} ${c.enabled ? 'enabled' : 'disabled'}`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const missing = missingLlmFields(config.provider, config, config.secretsSet ?? {});

  return (
    <Card className={clsx('flex flex-col p-5', config.isDefault && 'outline-1 -outline-offset-1 outline-primary')}>
      <div className="flex items-start gap-4">
        <ProviderTile provider={config.provider} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{config.name}</h3>
            {config.isDefault && (
              <Badge tone="brand" icon={<Star className="size-3" />}>
                Default
              </Badge>
            )}
            {!config.enabled && <Badge>Disabled</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-fg-secondary">
            {spec.label} · <span className="font-mono text-xs">{config.model}</span>
          </p>
          {config.baseUrl && <p className="truncate font-mono text-xs text-fg-muted">{config.baseUrl}</p>}
          {config.region && <p className="text-xs text-fg-muted">Region {config.region}</p>}
        </div>
        <Toggle label={`Enable ${config.name}`} checked={config.enabled} disabled={toggle.isPending} onChange={(v) => toggle.mutate(v)} />
      </div>

      <div className="mt-4 space-y-1" aria-live="polite">
        {missing.length > 0 && <p className="text-xs text-warning">Incomplete: {missing.join(', ')} still needed.</p>}
        {test.isPending ? <p className="text-xs text-fg-muted">Testing the connection…</p> : <TestOutcome result={result} config={config} />}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-border-subtle pt-4">
        <Button size="sm" variant="secondary" onClick={() => test.mutate()} loading={test.isPending} icon={<Zap className="size-4" />}>
          Test connection
        </Button>
        {!config.isDefault && (
          <Button size="sm" variant="ghost" onClick={() => makeDefault.mutate()} loading={makeDefault.isPending} icon={<Star className="size-4" />}>
            Make default
          </Button>
        )}
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onEdit} icon={<Pencil className="size-4" />} aria-label={`Edit ${config.name}`}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} icon={<Trash2 className="size-3 text-danger-text" />} aria-label={`Delete ${config.name}`} />
      </div>
    </Card>
  );
}

export function AiModelsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { currentOrgId, memberships } = useAuth();
  const orgId = currentOrgId ?? '';
  const orgName = memberships.find((m) => m.orgId === orgId)?.orgName;
  const scope = useScope(currentOrgId);
  const configs = useQuery({ queryKey: ['llm-configs', orgId], queryFn: () => api.llmConfigs(orgId), enabled: !!orgId });
  const [dialog, setDialog] = useState<{ open: boolean; existing: LlmConfigView | null }>({ open: false, existing: null });
  const [deleting, setDeleting] = useState<LlmConfigView | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => api.deleteLlmConfig(id),
    onSuccess: () => {
      toast.success('AI model removed');
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['llm-configs'] });
      qc.invalidateQueries({ queryKey: ['llm-status'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!orgId) return <EmptyState icon={<Bot className="size-6" />} title="No organization selected" description="Create an organization first." />;
  if (configs.isPending) return <PageLoader />;
  if (configs.isError) return <ErrorNotice error={configs.error} onRetry={() => configs.refetch()} />;

  const inScope = configs.data.filter((c) => c.projectId === scope.projectId);
  const list = inScope.sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name));
  const hasUsableDefault = list.some((c) => c.isDefault && c.enabled);
  const orgDefault = configs.data.find((c) => c.projectId === null && c.isDefault && c.enabled);
  const add = () => setDialog({ open: true, existing: null });
  const projectName = scope.project?.name;

  return (
    <div>
      <PageHeader
        eyebrow={orgName ?? 'Organization settings'}
        title="AI models"
        description="Plain-English steps (“Do something”, “Check something”) are carried out by an AI model. Connect one for the whole organization; a project can use its own model instead."
        actions={
          list.length > 0 && (
            <Button onClick={add} icon={<Plus className="size-4" />}>
              Add AI model
            </Button>
          )
        }
      />

      <div className="mb-6">
        <ScopePicker projects={scope.projects} projectId={scope.projectId} onChange={scope.setProjectId} />
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={<Bot className="size-6" />}
          title={projectName ? `${projectName} uses the organization's model` : 'No AI model set up yet'}
          description={
            projectName
              ? orgDefault
                ? `Runs of this project use “${orgDefault.name}” (${orgDefault.model}). Add a model here only if this project needs a different one.`
                : "There is no organization model yet either, so plain-English steps can't run. Add one for the whole organization or just for this project."
              : "Tests with plain-English steps can't run until an AI model is connected. Add one from OpenAI, Anthropic, AWS Bedrock, LiteLLM or Google Gemini."
          }
          action={
            <Button onClick={add} icon={<Plus className="size-4" />}>
              {projectName ? `Add a model for ${projectName}` : 'Add AI model'}
            </Button>
          }
        />
      ) : (
        <>
          {!hasUsableDefault && (
            <div role="status" className="mb-5 flex items-start gap-3 rounded-card bg-warning-subtle p-4 text-sm text-fg ring-1 ring-warning/40 ring-inset">
              <Plug className="mt-0.5 size-4 shrink-0" />
              {projectName
                ? `No enabled default model for ${projectName}, so it uses the organization's. Choose “Make default” below to use one of these instead.`
                : "No enabled default model. Choose “Make default” on one of the models below, otherwise plain-English steps can't run."}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {list.map((c) => (
              <ConfigCard key={c.id} config={c} onEdit={() => setDialog({ open: true, existing: c })} onDelete={() => setDeleting(c)} />
            ))}
          </div>
        </>
      )}

      <ConfigDialog
        open={dialog.open}
        existing={dialog.existing}
        orgId={orgId}
        projectId={scope.projectId}
        onClose={() => setDialog({ open: false, existing: null })}
      />

      <Dialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        size="sm"
        title={`Remove ${deleting?.name ?? 'this model'}?`}
        description={
          deleting?.isDefault
            ? deleting.projectId
              ? "This is the project's default model. The project falls back to the organization's model."
              : 'This is the default model. Until you choose another default, plain-English steps will not run.'
            : 'Its saved keys are deleted. Tests are not affected unless this is the default.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={del.isPending} onClick={() => deleting && del.mutate(deleting.id)}>
              Remove
            </Button>
          </>
        }
      >
        <span className="sr-only">Confirm removal</span>
      </Dialog>
    </div>
  );
}
